import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';
import client from '../../api/client';
import { setLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n';

const NATIVE_LABELS: Record<SupportedLanguage, string> = {
  he: 'עברית',
  en: 'English',
  ru: 'Русский',
};

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const current = (i18n.language as SupportedLanguage) ?? 'he';

  const handleSelect = (lang: SupportedLanguage) => {
    setLanguage(lang);
    setAnchorEl(null);
    // Best-effort: persist to user profile if logged in. Failures are non-fatal
    // (e.g. unauthenticated session, network error) — the localStorage write
    // already happened, so the UI keeps the choice locally.
    if (localStorage.getItem('hash_token')) {
      void client.put('/auth/me/language', { language: lang }).catch(() => undefined);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          color: 'rgba(255,255,255,0.85)',
          gap: 0.5,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
        }}
        aria-label="Change language"
      >
        <LanguageIcon fontSize="small" />
        <span style={{ fontSize: 12, fontWeight: 600 }}>{current.toUpperCase()}</span>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <MenuItem
            key={lang}
            selected={lang === current}
            onClick={() => handleSelect(lang)}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {lang === current ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText primary={NATIVE_LABELS[lang]} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
